import { useState } from 'react';

import { useCreateTelegramLinkCode, useTelegramChats, useUnbindTelegramChat } from '../../hooks/use-settings';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';

const ROLE_LABEL: Record<'leads_chat' | 'alerts_chat' | 'team_chat', string> = {
  leads_chat: 'Заявки',
  alerts_chat: 'Алерты',
  team_chat: 'Команда',
};

/**
 * U8: instructions + one-time `/bind <code>` generation, and the list of
 * chats already bound (with unbind). Binding itself happens in Telegram —
 * this panel only issues the code and reflects what got saved.
 */
export function TelegramChatsPanel() {
  const { data, isLoading } = useTelegramChats();
  const createCode = useCreateTelegramLinkCode();
  const unbind = useUnbindTelegramChat();
  const toast = useToast();
  const [issued, setIssued] = useState<{ kind: string; command: string } | null>(null);

  function generateCode(kind: 'leads_chat' | 'alerts_chat' | 'team_chat') {
    createCode.mutate(kind, {
      onSuccess: (res) => setIssued({ kind, command: res.command }),
      onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось создать код'),
    });
  }

  function doUnbind(kind: 'leads_chat' | 'alerts_chat' | 'team_chat') {
    unbind.mutate(kind, {
      onSuccess: () => toast.push('Чат отвязан', 'success'),
      onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось отвязать чат'),
    });
  }

  return (
    <div className="max-w-lg space-y-3 rounded border border-border p-3">
      <h3 className="text-sm font-semibold">Чаты для уведомлений</h3>
      <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-fg">
        <li>
          Создайте бота у <span className="font-mono">@BotFather</span> (или используйте существующего) и сохраните токен выше.
        </li>
        <li>Добавьте бота в нужный чат (группу или канал).</li>
        <li>Нажмите «Получить код» ниже и отправьте в чате команду вида «/bind код».</li>
      </ol>

      <div className="flex flex-wrap gap-2">
        {(['leads_chat', 'alerts_chat', 'team_chat'] as const).map((kind) => (
          <Button key={kind} type="button" variant="secondary" size="sm" onClick={() => generateCode(kind)} disabled={createCode.isPending}>
            Код для «{ROLE_LABEL[kind]}»
          </Button>
        ))}
      </div>
      {issued && (
        <div className="rounded border border-primary/30 bg-primary/5 p-2 text-xs" role="status">
          Отправьте в чате «{ROLE_LABEL[issued.kind as keyof typeof ROLE_LABEL]}»: <span className="font-mono">{issued.command}</span>
        </div>
      )}

      <div className="space-y-1">
        {isLoading && <p className="text-xs text-muted-fg">Загрузка…</p>}
        {!isLoading && (data?.chats.length ?? 0) === 0 && <p className="text-xs text-muted-fg">Пока ничего не привязано.</p>}
        {data?.chats.map((c) => (
          <div key={`${c.role}-${c.chat_id}`} className="flex items-center justify-between rounded border border-border px-2 py-1 text-sm">
            <span>
              <Badge tone="info" className="mr-2">
                {ROLE_LABEL[`${c.role}_chat`] ?? c.label}
              </Badge>
              {c.label} <span className="text-muted-fg">({c.chat_id})</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => doUnbind(`${c.role}_chat`)}
              disabled={unbind.isPending}
            >
              Отвязать
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
